import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Gallery from '../components/Gallery';
import { useGuest } from '../contexts/GuestContext';
import { useLanguage } from '../contexts/LanguageContext';

function GalleryPage() {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { guestGroup, guestToken } = useGuest();
  const { t, l } = useLanguage();
  const [albums, setAlbums] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [displayedPhotos, setDisplayedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlbums, setShowAlbums] = useState(!albumId);
  const [uploading, setUploading] = useState(false);
  const photosPerLoad = 24;

  const canUploadToAlbum = currentAlbum && guestGroup && 
    (currentAlbum.ownerGroupId === guestGroup.id || currentAlbum.id === 'all');

  useEffect(() => {
    loadData();
  }, [albumId]);

  useEffect(() => {
    if (albumId && albums.length > 0) {
      const album = albums.find(a => a.id === albumId);
      setCurrentAlbum(album);
      
      // Get photos for this album, including placeholders for missing ones
      const albumPhotos = album?.photoIds
        ? album.photoIds.map(photoId => {
            const photo = allPhotos.find(p => p.id === photoId);
            return photo || { id: photoId, url: '', thumbnailUrl: '', isMissing: true };
          })
        : [];
      
      setPhotos(albumPhotos);
      setDisplayedPhotos(albumPhotos.slice(0, photosPerLoad));
      setShowAlbums(false);
    } else if (!albumId && albums.length > 0) {
      setShowAlbums(true);
    }
  }, [albumId, albums, allPhotos]);

  useEffect(() => {
    if (!showAlbums) {
      const handleScroll = () => {
        if (displayedPhotos.length >= photos.length) return;
        
        const scrollPosition = window.innerHeight + window.scrollY;
        const threshold = document.documentElement.scrollHeight - 500;
        
        if (scrollPosition >= threshold) {
          loadMorePhotos();
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [displayedPhotos, photos, showAlbums]);

  const loadData = async () => {
    try {
      const response = await fetch('/api/photos');
      const data = await response.json();
      setAlbums(data.albums || []);
      setAllPhotos(data.photos || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load photos:', error);
      setLoading(false);
    }
  };

  const loadMorePhotos = () => {
    const currentLength = displayedPhotos.length;
    const morePhotos = photos.slice(currentLength, currentLength + photosPerLoad);
    if (morePhotos.length > 0) {
      setDisplayedPhotos([...displayedPhotos, ...morePhotos]);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('caption', 'Uploaded to album');
        formData.append('albumId', currentAlbum.id);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          await loadData();
        }
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleAlbumClick = (album) => {
    navigate(l(`/gallery/${album.id}`));
  };

  if (showAlbums) {
    return (
      <div className="gallery-page">
        <Navigation />
        <div className="gallery-header">
          <Link to={l('/')} className="back-link">{t('backToInvitation')}</Link>
          <h1>{t('photoAlbums')}</h1>
          <p style={{ color: '#8a8a8a' }}>{t('browseMemories')}</p>
        </div>
        <div className="albums-grid">
          {albums.map(album => {
            const albumPhotoCount = album.photoIds?.length || 0;
            const coverPhotoId = album.coverPhoto || album.photoIds?.[0];
            const coverPhotoObj = coverPhotoId ? allPhotos.find(p => p.id === coverPhotoId) : null;
            const coverPhoto = coverPhotoObj?.thumbnailUrl || coverPhotoObj?.url;
            
            return (
              <div 
                key={album.id} 
                className="album-card"
                onClick={() => handleAlbumClick(album)}
              >
                {coverPhoto ? (
                  <img src={coverPhoto} alt={album.name} loading="lazy" />
                ) : (
                  <div className="album-placeholder">{t('noPhotos')}</div>
                )}
                <div className="album-info">
                  <h3>{album.name}</h3>
                  <p>{t('photosCount', albumPhotoCount)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-page">
      <Navigation />
      <div className="gallery-header">
        <Link to={l('/gallery')} className="back-link">{t('backToAlbums')}</Link>
        <h1>{currentAlbum?.name || t('photoGallery')}</h1>
        {currentAlbum?.description && (
          <p style={{ color: '#8a8a8a' }}>{currentAlbum.description}</p>
        )}
        {canUploadToAlbum && (
          <div className="upload-section">
            <label className="upload-btn">
              {uploading ? t('uploading') : t('uploadPhotos')}
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        )}
      </div>
      <Gallery photos={displayedPhotos} allPhotos={photos} albumId={albumId} onPhotoDeleted={loadData} />
      <div className="loading">
        {loading ? t('loadingPhotos') : displayedPhotos.length >= photos.length ? 
          (photos.length > 0 ? t('allPhotosLoaded', photos.length) : t('noPhotosYet')) : 
          t('scrollForMore')}
      </div>
    </div>
  );
}

export default GalleryPage;
